import type { FactSetHostContext } from "../shared/factset/hostContextService";

const RETRY_DELAYS_MS = [500, 1000, 2000] as const;

interface DetectFactSetHostContextWithRetriesOptions {
  detect: (forceRefresh: boolean) => Promise<FactSetHostContext | null>;
  wait?: (delayMs: number) => Promise<void>;
  shouldStop?: () => boolean;
  onError?: (error: unknown) => void;
}

function waitFor(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function isAuthoritativeHostContext(
  hostContext: FactSetHostContext | null
): hostContext is FactSetHostContext {
  return (
    hostContext?.appType === "spreadsheet" ||
    hostContext?.appType === "presentation"
  );
}

export async function detectFactSetHostContextWithRetries({
  detect,
  wait = waitFor,
  shouldStop = () => false,
  onError,
}: DetectFactSetHostContextWithRetriesOptions): Promise<FactSetHostContext | null> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    if (shouldStop()) return null;

    let hostContext: FactSetHostContext | null = null;
    try {
      hostContext = await detect(attempt > 0);
    } catch (error) {
      onError?.(error);
    }

    if (shouldStop()) return null;
    if (isAuthoritativeHostContext(hostContext)) return hostContext;

    const retryDelayMs = RETRY_DELAYS_MS[attempt];
    if (retryDelayMs === undefined) return null;
    await wait(retryDelayMs);
  }

  return null;
}
